import React, { createContext, FC, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';
import { playerRouteMatch } from 'hooks/usePlayerRouter';
import YouboraTracking from 'providers/player/YouboraTracking';
import { usePersistentState } from 'hooks/useStorage/useStorage';
import { AuthError, UserInformation } from 'types/AuthTypes';
import { AccountInfo } from 'types/Account';
import { setLocalStorage, setLocalStorageString, removeLocalStorage, getLocalStorageString } from 'utils/fnStorage';
import { useDataFetcher } from 'hooks/useDataFetcher/useDataFetcher';
import GSApi from 'api/Api';

import { useConfig } from 'providers/useConfig/ConfigContext';
import { useApp } from 'providers/useApp/AppContext';
import { AlertDialog } from 'components/AlertDialog/AlertDialog';
import translate from 'utils/fnTranslate';
import DRM from 'providers/player/DRM';
import CastHandler from 'providers/cast/CastHandler';
import { useSSE, SSEEvent, SSEAccountData } from '../../hooks/useSSE';
import {
    init,
    login,
    logout,
    userInfo,
    fetchCastTokens,
    setSignedOutNoticeHandler,
    resetSignedOutNotice,
    completeAccountSession,
} from './AuthService';

export const isAuthError = (value: any): value is AuthError => value && 'code' in value && 'msg' in value;

const useAuthService = () => {
    const [postLoginAction, setPostLoginAction] = useState<boolean>(false);
    const [postLoginPath, setPostLoginPath] = useState<string>(null);
    const [redirectAfterLogin, setRedirectAfterLogin] = useState(false);
    const [isLoggedIn, setLoggedIn] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [isGuestAlert, setIsGuestAlert] = useState(false);
    const accountRetrievedAtRef = useRef<number>(0);
    const [sseEnabled, setSseEnabled] = useState(false);
    const [cacheKey, setCacheKey] = useState<string | null>(() => getLocalStorageString('gstr-cache-key'));
    const hasFetchedRef = useRef(false);
    const initCalledRef = useRef(false);
    const [tokenReady, setTokenReady] = useState(false);

    const [isLogOutAlert, setIsLogOutAlert] = useState(false);
    const [isSignedOutNotice, setIsSignedOutNotice] = useState(false);
    const [isInhibitionAlert, setIsInhibitionAlert] = useState(false);
    const hasShownInhibitionAlertRef = useRef(false);
    const [guestAlertClosePath, setGuestAlertClosePath] = useState(null);
    const { store: isGuest, set: setIsGuest } = usePersistentState('isGuestUser');
    const { store: cpeId, set: setCpeId } = usePersistentState('cpeId');

    const { config } = useConfig();
    const { appLanguage } = useApp();
    const queryClient = useQueryClient();

    const [userInfoDetailsResponse, setUserInfoDetailsResponse] = useState<UserInformation | null>(null);

    const { response: accountInfoDetailsResponse, fetcher: accountInfoDetailsFetcher } = useDataFetcher<
        AccountInfo & { status?: number },
        null
    >(() => GSApi.fetchAccountInfo());

    const isInhibited = !!accountInfoDetailsResponse?.service_codes?.includes('inhibition');

    const isRefreshingAccountInfo = useRef(false);

    const logUserIn = () => {
        resetSignedOutNotice();
        removeLocalStorage('forceLogin');
        login(
            {
                locale: appLanguage,
                scope: config?.app_config?.auth?.scope || null,
            },
            (state) => setLoggedIn(state)
        );
    };

    const logUserOut = (forceLogin = false, local = false) => {
        hasShownInhibitionAlertRef.current = false;
        setIsInhibitionAlert(false);
        if (local) {
            setLoggedIn(false);
            setLocalStorage('wrongPinAttempts', null);
            setLocalStorage('pinLock', null);
            setLocalStorage('cpeId', null);
            setLocalStorage('playbackPositions', null);
        } else {
            if (forceLogin) {
                removeLocalStorage('guestRefreshToken');
                removeLocalStorage('guestToken');
                removeLocalStorage('refreshToken');
                removeLocalStorage('clientId');
                setLocalStorage('forceLogin', true);
            } else {
                removeLocalStorage('forceLogin');
            }
            setIsGuest(false);
            setLoggedIn(false);
            setTokenReady(false);
            setUserInfoDetailsResponse(null);
            setLocalStorage('playbackPositions', null);

            logout((state) => {
                setLoggedIn(state);
                setLocalStorage('wrongPinAttempts', null);
                setLocalStorage('pinLock', null);
                setLocalStorage('cpeId', null);
            });
        }
    };

    const drmInit = (userData) => {
        setCpeId(userData?.deviceId);

        if (!userData?.guest) {
            setIsGuest(false);

            DRM.init();
            setCpeId(DRM.getDeviceId() || userData?.deviceId);
            setInitialized(true);

            removeLocalStorage('guestRefreshToken');
            removeLocalStorage('guestToken');
        } else {
            setIsGuest(userData?.guest);
            setInitialized(true);
        }
    };

    useEffect(() => {
        if (!tokenReady) {
            hasFetchedRef.current = false;
            return;
        }

        if (accountInfoDetailsResponse) {
            completeAccountSession();
            return;
        }

        if (hasFetchedRef.current) {
            return;
        }

        hasFetchedRef.current = true;
        accountInfoDetailsFetcher(null)
            .then((result) => {
                const account = (result as { response?: AccountInfo })?.response;
                if (account?.cache_key) {
                    setLocalStorageString('gstr-cache-key', account.cache_key);
                    setCacheKey(account.cache_key);
                }
            })
            .finally(() => completeAccountSession());
    }, [tokenReady, accountInfoDetailsResponse]);

    useEffect(() => {
        if (!accountInfoDetailsResponse) return;

        if (accountInfoDetailsResponse.cache_key) {
            setCacheKey(accountInfoDetailsResponse.cache_key);
            setLocalStorageString('gstr-cache-key', accountInfoDetailsResponse.cache_key);
        }

        if (accountInfoDetailsResponse.sse_url) {
            accountRetrievedAtRef.current = Date.now();
            setSseEnabled(true);
        }
    }, [accountInfoDetailsResponse]);

    useEffect(() => {
        if (isInhibited && !hasShownInhibitionAlertRef.current) {
            hasShownInhibitionAlertRef.current = true;
            setIsInhibitionAlert(true);
        }
    }, [isInhibited]);

    useEffect(() => {
        CastHandler.setTokenProvider(fetchCastTokens);
    }, []);

    useEffect(() => {
        setSignedOutNoticeHandler(() => {
            setIsGuest(false);
            setLoggedIn(false);
            setTokenReady(false);
            setUserInfoDetailsResponse(null);
            setIsSignedOutNotice(true);
        });

        return () => setSignedOutNoticeHandler(null);
    }, []);

    // The API cache key can change mid-session. When it changes during an active chromecast, notify the receiver.
    const lastSentCacheKeyRef = useRef<string | null | undefined>(undefined);
    useEffect(() => {
        if (lastSentCacheKeyRef.current === undefined) {
            lastSentCacheKeyRef.current = cacheKey;
            return;
        }
        if (cacheKey && cacheKey !== lastSentCacheKeyRef.current) {
            lastSentCacheKeyRef.current = cacheKey;
            if (CastHandler.isCast()) {
                CastHandler.sendCacheKey(cacheKey);
            }
            queryClient.invalidateQueries({ queryKey: ['continueWatching'] });
            queryClient.invalidateQueries({ queryKey: ['collection'] });
            queryClient.invalidateQueries({ queryKey: ['page'] });
        }
    }, [cacheKey]);

    const refreshAccountInfo = useCallback(() => {
        if (isRefreshingAccountInfo.current) return;
        isRefreshingAccountInfo.current = true;
        accountInfoDetailsFetcher(null).finally(() => {
            isRefreshingAccountInfo.current = false;
        });
    }, []);

    const handleSSEEvent = useCallback(
        (event: SSEEvent) => {
            if (
                event.data?.resource === 'account' ||
                event.data?.resource === 'service_purchases' ||
                event.data?.resource === 'vod_purchases'
            ) {
                refreshAccountInfo();
            }
            if (event.data?.resource === 'service_purchases' || event.data?.resource === 'account') {
                window.dispatchEvent(new CustomEvent('service-purchase-synced'));
            }
        },
        [refreshAccountInfo]
    );

    const fetchSSEAccount = useCallback(async (): Promise<SSEAccountData | null> => {
        const retrievedAt = Date.now();
        const result = (await accountInfoDetailsFetcher(null)) as { response?: AccountInfo } | null;
        const account = result?.response ?? null;
        if (!account?.sse_url) return null;
        return { account, retrievedAt };
    }, []);

    const initialAccount: SSEAccountData | null =
        accountInfoDetailsResponse && accountInfoDetailsResponse.sse_url
            ? { account: accountInfoDetailsResponse, retrievedAt: accountRetrievedAtRef.current }
            : null;

    useEffect(() => {
        if (!tokenReady) setSseEnabled(false);
    }, [tokenReady]);

    const { isConnected: sseConnected } = useSSE({
        enabled: sseEnabled,
        initialAccount,
        fetchAccount: fetchSSEAccount,
        onEvent: handleSSEEvent,
    });

    useEffect(() => {
        if (config && !initialized && !initCalledRef.current) {
            initCalledRef.current = true;
            init(
                (authenticated) => {
                    if (authenticated) {
                        userInfo(async (userData: any) => {
                            setCpeId(null);

                            setLoggedIn(authenticated);
                            setTokenReady(true);
                            setUserInfoDetailsResponse(userData);

                            if (userData) {
                                drmInit(userData);
                            } else {
                                setInitialized(true);
                            }
                        });
                    } else {
                        completeAccountSession();
                        setInitialized(true);
                    }
                },
                () => {
                    completeAccountSession();
                    logUserOut();
                }
            );
        }
    }, [config]);

    return {
        logUserIn,
        logUserOut,
        setRedirectAfterLogin,
        postLoginAction,
        setPostLoginAction,
        setPostLoginPath,

        isLoggedIn,
        isGuest,
        authApiInitialized: initialized,
        redirectAfterLogin,
        postLoginPath,
        drmInit,

        cacheKey,
        setCacheKey,
        refreshAccountInfo,
        sseConnected,

        guestAlert: isGuestAlert,
        guestAlertClosePath,
        closeGuestAlert: () => {
            setIsGuestAlert(false);
            setGuestAlertClosePath(null);
        },
        openGuestAlert: (onClosePath?: string) => {
            if (isSignedOutNotice) return;
            setIsGuestAlert(true);
            setGuestAlertClosePath(onClosePath);
        },

        isLogOutAlert,
        openLogOutAlert: () => setIsLogOutAlert(true),
        closeLogOutAlert: () => setIsLogOutAlert(false),

        isSignedOutNotice,

        isInhibited,
        isInhibitionAlert,
        openInhibitionAlert: () => setIsInhibitionAlert(true),
        closeInhibitionAlert: () => setIsInhibitionAlert(false),

        cpeId,

        userInfoDetails: userInfoDetailsResponse,
        accountInfo: accountInfoDetailsResponse,
    };
};

export const AuthContext = createContext<ReturnType<typeof useAuthService>>(null);

export const AuthProvider = ({ children }) => {
    const auth = useAuthService();

    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export const useGuestAwareClickHandler = () => {
    const { isGuest, openGuestAlert } = useAuth();

    return (event: React.MouseEvent<any>, continueCallback) => {
        if (isGuest) {
            event.preventDefault();
            event.stopPropagation();

            event.bubbles = false;

            openGuestAlert();

            return;
        }

        if (continueCallback) {
            continueCallback(event);
        }
    };
};

export const GuestAlert: FC = () => {
    const { guestAlert, guestAlertClosePath, closeGuestAlert, logUserIn, isSignedOutNotice } = useAuth();
    const navigate = useNavigate();

    if (!guestAlert || isSignedOutNotice) return null;

    return (
        <AlertDialog
            title={translate('ERR_LOGIN_REQUIRED_HEADER')}
            bodyText={translate('ERR_LOGIN_REQUIRED_BODY')}
            buttons={[
                {
                    text: translate('CONTINUE_AS_GUEST_BUTTON'),
                    onClick: () => {
                        if (guestAlertClosePath) {
                            navigate(guestAlertClosePath, { replace: true });
                        }

                        closeGuestAlert();
                    },
                },
                {
                    text: translate('SCREEN_LOGIN_BUTTON'),
                    onClick: () => logUserIn(),
                },
            ]}
        />
    );
};

export const SignedOutNotice: FC = () => {
    const { isSignedOutNotice, logUserIn } = useAuth();

    if (!isSignedOutNotice) return null;

    return (
        <AlertDialog
            title={translate('SCREEN_DEVICE_MANAGEMENT_SIGNOUT_NOTICE_HEADER')}
            buttons={[
                {
                    text: translate('SCREEN_LOGIN_BUTTON'),
                    onClick: () => logUserIn(),
                },
            ]}
        />
    );
};

// Navigate user back to home page if it tries to access any player route via URL
export const InhibitionPlayerRouteGuard: FC = () => {
    const { isInhibited, openInhibitionAlert } = useAuth();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isInhibited) return;
        const { type } = playerRouteMatch(pathname);
        if (type) {
            openInhibitionAlert();
            navigate('/', { replace: true });
        }
    }, [isInhibited, pathname]);

    return null;
};

export const InhibitionAlert: FC = () => {
    const { isInhibitionAlert, closeInhibitionAlert } = useAuth();

    if (!isInhibitionAlert) return null;

    return (
        <AlertDialog
            title={translate('NOTIFICATION_INHIBITION_FOUND_TITLE')}
            bodyText={<span dangerouslySetInnerHTML={{ __html: translate('NOTIFICATION_INHIBITION_FOUND_BODY') }} />}
            elevated
            buttons={[
                {
                    text: translate('CLOSE_BUTTON'),
                    onClick: () => closeInhibitionAlert(),
                },
            ]}
        />
    );
};

export const SignOut: FC = () => {
    const { isLogOutAlert, closeLogOutAlert, logUserOut } = useAuth();

    const handleLogout = () => {
        const youboraTracking = YouboraTracking.getInstance();

        youboraTracking.handleLogout();

        logUserOut(true);
    };

    if (!isLogOutAlert) return null;

    return (
        <AlertDialog
            title={translate('SCREEN_SIGNOUT_HEADER')}
            bodyText={translate('SCREEN_SIGNOUT_BODY')}
            buttons={[
                {
                    text: translate('CANCEL_BUTTON'),
                    onClick: () => closeLogOutAlert(),
                },
                {
                    text: translate('SIGNOUT_BUTTON'),
                    onClick: handleLogout,
                },
            ]}
        />
    );
};
